import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import {
  TelephonyAgent,
  TelephonyAgentAnalytics,
  TelephonyCallAnalytics,
  fetchCallAnalytics,
  formatCount,
  formatPercent,
  formatTalkMinutes,
  formatTalkTime,
  telephonyApi,
} from '../../../Telephony/telephonyApi';
import { getTelephonyAnalyticsScope, isOutSourcingTeam, isSalesTeam } from '../../../Telephony/roles';
import { useEmailAccessConfig } from '../../../utils/emailAccessConfig';
import MyCallsList from '../../../Telephony/MyCallsList';
import { Page, PageHeader, Card, Button, Field, TextInput, Select, Toolbar, Table, Loader, EmptyState } from '../../../ui';
import StatTile, { StatTileTone } from './ui-pending/StatTile';
import './telephony.scss';

/** yyyy-mm-dd in the user's own timezone — `toISOString()` would shift the day near midnight. */
const isoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

const daysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

const RANGE_PRESETS: { label: string; days: number }[] = [
  { label: 'Today', days: 0 },
  { label: '7 days', days: 6 },
  { label: '30 days', days: 29 },
];

/** Summary cards, in the order the metrics were specified. */
const CARDS: {
  key: string;
  label: string;
  hint: string;
  value: (t: TelephonyCallAnalytics['totals']) => string;
  unit?: string;
}[] = [
  { key: 'total', label: 'Total calls', hint: 'Every call placed or received in the range', value: (t) => formatCount(t.totalCalls) },
  { key: 'outbound', label: 'Outbound', hint: 'Calls placed to customers', value: (t) => formatCount(t.outboundCalls) },
  { key: 'inbound', label: 'Inbound', hint: 'Calls received from customers', value: (t) => formatCount(t.inboundCalls) },
  {
    key: 'connected',
    label: 'Connected',
    hint: 'Reached the customer and ended normally — voicemail pickups and unanswered calls excluded',
    value: (t) => formatCount(t.connectedCalls),
  },
  {
    key: 'connectivity',
    label: 'Connectivity',
    hint: 'Connected calls as a share of total calls',
    value: (t) => formatPercent(t.connectivityPct),
  },
  {
    key: 'talk',
    label: 'Talk time',
    hint: 'Total minutes connected, summed across connected calls',
    value: (t) => formatTalkMinutes(t.talkSeconds),
    unit: 'min',
  },
  {
    key: 'avgTalk',
    label: 'Avg talk time',
    hint: 'Talk time divided by connected calls',
    value: (t) => formatTalkTime(t.avgTalkSeconds),
  },
  {
    key: 'uniqueNumbers',
    label: 'Unique numbers',
    hint: 'Distinct customer numbers in the range — one customer called five times counts once',
    // Absent rather than 0 when the platform is an older deploy that does not
    // report it yet, so a version skew shows as "no answer" instead of a
    // wrong one. formatCount would turn undefined into a confident 0.
    value: (t) => (t.uniqueNumbers == null ? '—' : formatCount(t.uniqueNumbers)),
  },
];

type SortKey = keyof Pick<
  TelephonyAgentAnalytics,
  | 'totalCalls'
  | 'outboundCalls'
  | 'inboundCalls'
  | 'connectedCalls'
  | 'connectivityPct'
  | 'talkSeconds'
  | 'avgTalkSeconds'
> | 'agentName';

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: 'agentName', label: 'Agent' },
  { key: 'totalCalls', label: 'Total', numeric: true },
  { key: 'outboundCalls', label: 'Outbound', numeric: true },
  { key: 'inboundCalls', label: 'Inbound', numeric: true },
  { key: 'connectedCalls', label: 'Connected', numeric: true },
  { key: 'connectivityPct', label: 'Connectivity', numeric: true },
  { key: 'talkSeconds', label: 'Talk time', numeric: true },
  { key: 'avgTalkSeconds', label: 'Avg talk', numeric: true },
];

const METRIC_CELL: { key: SortKey; render: (a: TelephonyAgentAnalytics) => string }[] = [
  { key: 'totalCalls', render: (a) => formatCount(a.totalCalls) },
  { key: 'outboundCalls', render: (a) => formatCount(a.outboundCalls) },
  { key: 'inboundCalls', render: (a) => formatCount(a.inboundCalls) },
  { key: 'connectedCalls', render: (a) => formatCount(a.connectedCalls) },
  { key: 'connectivityPct', render: (a) => formatPercent(a.connectivityPct) },
  { key: 'talkSeconds', render: (a) => formatTalkTime(a.talkSeconds) },
  { key: 'avgTalkSeconds', render: (a) => formatTalkTime(a.avgTalkSeconds) },
];

/**
 * Green above 60%, amber above 35%, red below — a read at a glance, not a hard
 * SLA. `totalCalls` is passed in so an empty range renders a plain 0.0% instead
 * of an alarming red one: nobody missed a target on a day with no calls.
 */
const connectivityTone = (pct: number, totalCalls: number): StatTileTone | undefined => {
  if (totalCalls <= 0) return undefined;
  return pct >= 60 ? 'success' : pct >= 35 ? 'warning' : 'danger';
};

/** Table-cell class for a connectivity value (numeric + tone colour). */
const connectivityCellClass = (pct: number, totalCalls: number): string => {
  const tone = connectivityTone(pct, totalCalls);
  return tone ? `ds-num tel-tone--${tone}` : 'ds-num';
};

/**
 * Telephony → Analytics: call volume, connectivity and talk time over a date
 * range, for all agents combined and agent by agent.
 *
 * The platform does the aggregation (GET /v1/analytics/calls) and returns the
 * overall totals folded up from the same agent rows shown in the table, so the
 * cards and the table can never disagree.
 */
const TelephonyAnalyticsPage: React.FC = () => {
  // Telephony admins (ROLE_ADMIN) get the team/agent pickers. Outsourced
  // agents on "outSourcingAnalytics" see only the outsourcing team's agents with
  // no team picker; other outsourced agents see only their own numbers.
  // See getTelephonyAnalyticsScope in roles.ts.
  //
  // This hides the controls, but it is NOT what enforces the rule: the
  // allowlist here reads localStorage, which the browser can edit. The
  // platform re-checks the caller's own CMS token and 403s a non-manager who
  // names another agent. See analytics.controller.ts.
  // Re-render once adminEmailAccess.json lands — the manager/telephony-only lists live there.
  useEmailAccessConfig();
  const scope = getTelephonyAnalyticsScope();
  const manager = scope !== 'self';
  const outSourcing = scope === 'outSourcing';
  // SALES_ADMIN keeps the team picker but only with the Sales and Sales & Renewal
  // teams; the platform enforces the same two teams (analytics.controller.ts).
  const salesOnly = scope === 'sales';
  const [data, setData] = useState<TelephonyCallAnalytics | null>(null);
  const [agentList, setAgentList] = useState<TelephonyAgent[]>([]);
  const [teamList, setTeamList] = useState<{ id: string; key: string; name: string }[]>([]);
  const [agentFilter, setAgentFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [direction, setDirection] = useState('');
  // Opens on today. daysAgo(0) rather than a bare isoDay(new Date()) so this
  // stays tied to the "Today" preset — the two have to agree, or the page
  // loads with no preset looking selected.
  const [fromDate, setFromDate] = useState(daysAgo(0));
  const [toDate, setToDate] = useState(isoDay(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('totalCalls');

  useEffect(() => {
    if (!manager) return; // no team/agent pickers to populate
    telephonyApi<{ id: string; key: string; name: string; isActive: boolean }[]>('/v1/teams')
      .then((teams) => setTeamList(teams.filter((t) => t.isActive)))
      .catch(() => undefined)
      .finally(() => setTeamsLoaded(true));
    telephonyApi<TelephonyAgent[]>('/v1/agents')
      .then(setAgentList)
      .catch(() => undefined);
  }, [manager]);

  // Outsourced agents are always pinned to their own team, so "All agents" never means the whole org.
  const outSourcingTeamId = outSourcing ? teamList.find(isOutSourcingTeam)?.id : undefined;

  const load = useCallback(() => {
    if (outSourcing && !outSourcingTeamId) {
      // Don't query unscoped while /v1/teams is still loading; say so if it has no outsourcing team.
      if (teamsLoaded) {
        setError('No outsourcing team found in Telephony → Teams.');
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    fetchCallAnalytics({
      // Local start/end of day, sent as ISO timestamps — same convention as Logs,
      // so both pages report on the same set of calls for the same dates.
      from: fromDate ? new Date(`${fromDate}T00:00:00`).toISOString() : undefined,
      to: toDate ? new Date(`${toDate}T23:59:59.999`).toISOString() : undefined,
      // Omitted entirely for a non-manager: the server derives the scope from
      // the token, and sending either one is a deliberate 403 rather than a
      // silent rescope.
      agentId: manager ? agentFilter || undefined : undefined,
      teamId: outSourcing
        ? agentFilter
          ? undefined
          : outSourcingTeamId
        : manager
          ? teamFilter || undefined
          : undefined,
      direction: direction || undefined,
    })
      .then(setData)
      .catch((e: Error) => {
        setError(e.message);
        toast.error(`Analytics: ${e.message}`);
      })
      .finally(() => setLoading(false));
  }, [manager, outSourcing, outSourcingTeamId, teamsLoaded, fromDate, toDate, agentFilter, teamFilter, direction]);

  useEffect(load, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const sorted = [...data.agents];
    sorted.sort((a, b) => {
      if (sortKey === 'agentName') {
        return (a.agentName ?? 'zzz').localeCompare(b.agentName ?? 'zzz');
      }
      return ((b[sortKey] as number) ?? 0) - ((a[sortKey] as number) ?? 0);
    });
    return sorted;
  }, [data, sortKey]);

  const activePreset = RANGE_PRESETS.find(
    (p) => fromDate === daysAgo(p.days) && toDate === isoDay(new Date()),
  );

  const selectedAgentName = agentFilter
    ? agentList.find((a) => a.id === agentFilter)?.name ?? 'Selected agent'
    : null;

  const pickerTeams = salesOnly ? teamList.filter(isSalesTeam) : teamList;
  const visibleAgents = outSourcing
    ? agentList.filter((a) => isOutSourcingTeam(a.team))
    : teamFilter
    ? agentList.filter(
        (a) => a.team?.key === teamList.find((t) => t.id === teamFilter)?.key,
      )
    : salesOnly
    ? agentList.filter((a) => isSalesTeam(a.team))
    : agentList;

  /**
   * What team to print under an agent's name in the breakdown: their current
   * team. The server's teamId filter matches that same team, so under a team
   * filter every row already belongs to the selected team.
   */
  const teamLabel = (a: TelephonyAgentAnalytics): string => a.teamName ?? '—';

  const teamOptions = [
    { value: '', label: salesOnly ? 'All sales teams' : 'All teams' },
    ...pickerTeams.map((t) => ({ value: t.id, label: t.name })),
  ];
  const agentOptions = [
    { value: '', label: 'All agents' },
    ...visibleAgents.map((a) => ({ value: a.id, label: a.name })),
  ];
  const directionOptions = [
    { value: '', label: 'All calls' },
    { value: 'OUTBOUND', label: 'Outgoing' },
    { value: 'INBOUND', label: 'Incoming' },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title={manager ? 'Call analytics' : 'My call analytics'}
        subtitle={
          <>
            {manager ? selectedAgentName ?? 'All agents' : 'Your calls only'} ·{' '}
            {fromDate || 'any date'} to {toDate || 'any date'}
          </>
        }
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <div className="tel-presets" role="group" aria-label="Date range presets">
            {RANGE_PRESETS.map((p) => (
              <Button
                key={p.label}
                size="sm"
                variant={activePreset?.label === p.label ? 'primary' : 'secondary'}
                aria-pressed={activePreset?.label === p.label}
                onClick={() => {
                  setFromDate(daysAgo(p.days));
                  setToDate(isoDay(new Date()));
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Field label="From">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="date"
                max={toDate || undefined}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="date"
                min={fromDate || undefined}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            )}
          </Field>
          {manager && !outSourcing ? (
            <Field label="Team">
              {(ctl) => (
                <Select
                  {...ctl}
                  options={teamOptions}
                  value={teamFilter}
                  onChange={(e) => {
                    setTeamFilter(e.target.value);
                    setAgentFilter('');
                  }}
                />
              )}
            </Field>
          ) : null}
          {manager ? (
            <Field label="Agent">
              {(ctl) => (
                <Select {...ctl} options={agentOptions} value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} />
              )}
            </Field>
          ) : null}
          <Field label="Direction">
            {(ctl) => (
              <Select {...ctl} options={directionOptions} value={direction} onChange={(e) => setDirection(e.target.value)} />
            )}
          </Field>
        </Toolbar>
      </Card>

      {error ? (
        <EmptyState
          icon={<AlertTriangle size={22} aria-hidden />}
          title="Couldn't load analytics"
          description={error}
          action={
            <Button size="sm" variant="secondary" onClick={load}>
              Try again
            </Button>
          }
        />
      ) : loading && !data ? (
        <Loader label="Loading analytics…" />
      ) : (
        <>
          <div className={loading ? 'tel-stats tel-stale' : 'tel-stats'} aria-busy={loading || undefined}>
            {CARDS.map((card) => (
              <StatTile
                key={card.key}
                label={card.label}
                hint={card.hint}
                value={data ? card.value(data.totals) : '—'}
                unit={card.unit}
                tone={
                  card.key === 'connectivity' && data
                    ? connectivityTone(data.totals.connectivityPct, data.totals.totalCalls)
                    : undefined
                }
              />
            ))}
          </div>

          {manager ? (
            <Card
              flush
              title="Agent-wise breakdown"
              actions={
                <span className="ds-text-subtle">
                  {loading
                    ? 'Updating…'
                    : `${rows.length} ${rows.length === 1 ? 'agent' : 'agents'} with calls`}
                </span>
              }
            >
              <Table caption="Agent-wise call analytics">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className={c.numeric ? 'ds-num tel-sortable' : 'tel-sortable'}
                        aria-sort={sortKey === c.key ? 'descending' : undefined}
                        onClick={() => setSortKey(c.key)}
                        title={`Sort by ${c.label}`}
                      >
                        {c.label}
                        {sortKey === c.key ? ' ▾' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS.length}>
                        <EmptyState
                          title="No calls in this date range."
                          description="Try widening the dates or clearing the team and agent filters."
                        />
                      </td>
                    </tr>
                  ) : (
                    rows.map((a) => {
                      const unassigned = a.agentId === null;
                      return (
                        <tr key={a.agentId ?? 'unassigned'}>
                          <td>
                            <div className={unassigned ? 'ds-strong ds-text-muted fst-italic' : 'ds-strong'}>
                              {a.agentName ?? 'Unassigned'}
                            </div>
                            <span className="tel-sub">
                              {unassigned
                                ? 'Calls that never reached an agent'
                                : teamLabel(a)}
                            </span>
                          </td>
                          {METRIC_CELL.map((cell) => (
                            <td
                              key={cell.key}
                              className={
                                cell.key === 'connectivityPct'
                                  ? connectivityCellClass(a.connectivityPct, a.totalCalls)
                                  : 'ds-num'
                              }
                            >
                              {cell.render(a)}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {data && rows.length > 0 ? (
                  <tfoot>
                    <tr>
                      <td>All agents</td>
                      <td className="ds-num">{formatCount(data.totals.totalCalls)}</td>
                      <td className="ds-num">{formatCount(data.totals.outboundCalls)}</td>
                      <td className="ds-num">{formatCount(data.totals.inboundCalls)}</td>
                      <td className="ds-num">{formatCount(data.totals.connectedCalls)}</td>
                      <td className={connectivityCellClass(data.totals.connectivityPct, data.totals.totalCalls)}>
                        {formatPercent(data.totals.connectivityPct)}
                      </td>
                      <td className="ds-num">{formatTalkTime(data.totals.talkSeconds)}</td>
                      <td className="ds-num">{formatTalkTime(data.totals.avgTalkSeconds)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </Table>

              <div className="tel-flush-pad">
                <p className="tel-note">
                  A call counts as <strong>connected</strong> when it reached the customer and ended
                  normally — calls nobody answered, and calls picked up by voicemail, are excluded.
                  Talk time is summed over connected calls only, and the average is per connected
                  call. Agents with no calls in this range are not listed.
                </p>
              </div>
            </Card>
          ) : (
            <>
              <MyCallsList fromDate={fromDate} toDate={toDate} direction={direction} />
              <p className="tel-note">
                These are your own calls only. A call counts as{' '}
                <strong>connected</strong> when it reached the customer and ended
                normally — calls nobody answered, and calls picked up by voicemail, are
                excluded. Talk time is summed over connected calls only, and the average
                is per connected call.
              </p>
            </>
          )}
        </>
      )}
    </Page>
  );
};

export default TelephonyAnalyticsPage;
